import { useState, useEffect, useCallback } from 'react';
import { getChapterConfigsBySchoolAndUser, createSchoolChapterConfig, createUserChapterConfig, getUserByEmail, deleteUserChapterConfig, updateChapterConfig, updateStudentSyllabus, listChapters, listActivitiesByChapter, setDefaultConfig } from '@dataconnect/generated';

const ChapterConfigPage = ({ selectedStudent, onBack, user, isAdmin: propIsAdmin }) => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newConfig, setNewConfig] = useState({
    configName: '',
    selectedChapters: {} // { chapterId: { selected: true } }
  });
  const [deleting, setDeleting] = useState(null);
  const [editing, setEditing] = useState(null);
  const [selecting, setSelecting] = useState(null);
  const [editConfig, setEditConfig] = useState({
    configName: '',
    selectedChapters: {}
  });
  const [allChapters, setAllChapters] = useState([]);
  const [activeTab, setActiveTab] = useState('LESSON'); // Active chapter type tab
  const [isSchoolLevelConfig, setIsSchoolLevelConfig] = useState(false); // Default to personal-level

  // Determine if the user has global admin rights
  const isGlobalAdmin = propIsAdmin || (user?.email && (
    user.email.includes('admin') || 
    ['admin@zenokids.com', 'saritha@zenokids.com', 'sarithakamath@gmail.com'].includes(user.email)
  ));

  const loadConfigs = useCallback(async (showLoading = true) => {
    if (!selectedStudent) return;

    // Check if we have the required data
    if (!user?.email) {
      console.error('user.email is undefined:', user);
      setError('User authentication information is missing.');
      return;
    }

    try {
      if (showLoading) setLoading(true);
      setError(null);

      // First get the internal User UUID using the email
      const userResult = await getUserByEmail({ email: user.email });
      if (!userResult.data?.users?.length) {
        setError('User not found in the system. Please ensure your account is properly set up.');
        if (showLoading) setLoading(false);
        return;
      }

      const internalUserId = userResult.data.users[0].id;

      const params = {
        userId: internalUserId,
        grade: selectedStudent.gradeLevel,
        // Only include schoolId if student has a school
        ...(selectedStudent.school?.id && { schoolId: selectedStudent.school.id })
      };

      const result = await getChapterConfigsBySchoolAndUser(params);

      if (result.data) {
        // Combine school and user configs
        const allConfigs = [
          ...(result.data.schoolConfigs || []),
          ...(result.data.userConfigs || [])
        ];
        setConfigs(allConfigs);
      }
    } catch (err) {
      console.error('Failed to load chapter configs:', err);
      setError('Failed to load chapter configurations. Please try again.');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [selectedStudent, user]);

  useEffect(() => {
    loadConfigs(true);
  }, [loadConfigs]);

  // Load chapters when form is opened
  useEffect(() => {
    const loadChapters = async () => {
      if (!showCreateForm && !editing) return;
      
      try {
        const chaptersResult = await listChapters();
        if (chaptersResult.data?.chapters) {
          // Filter chapters by grade
          const gradeChapters = chaptersResult.data.chapters.filter(
            ch => ch.grade === selectedStudent.gradeLevel
          );
          setAllChapters(gradeChapters);
        }
      } catch (err) {
        console.error('Failed to load chapters:', err);
      }
    };

    loadChapters();
  }, [showCreateForm, editing, selectedStudent]);

  const toggleChapter = (chapterId, isCreate = true) => {
    const setConfig = isCreate ? setNewConfig : setEditConfig;
    
    setConfig(prev => ({
      ...prev,
      selectedChapters: {
        ...prev.selectedChapters,
        [chapterId]: {
          ...prev.selectedChapters[chapterId],
          selected: !prev.selectedChapters[chapterId]?.selected
        }
      }
    }));
  };

  // Convert selectedChapters to chapterConfig (structure only) and activityData (full content)
  // Helper function to compute merged practice data by activity type
  const computePracticeData = (activityDataMap) => {
    const practiceMap = {};

    // Iterate through all activities in activityData
    Object.entries(activityDataMap).forEach(([activityId, activityInfo]) => {
      const activityType = activityInfo.activityType;
      const content = activityInfo.content;

      if (!practiceMap[activityType]) {
        // First occurrence of this activity type
        practiceMap[activityType] = {
          activityType: activityType,
          content: JSON.parse(JSON.stringify(content)) // Deep copy
        };
      } else {
        // Merge with existing activity of same type
        const existing = practiceMap[activityType].content;
        
        // Merge based on activity type
        switch (activityType) {
          case 'WORD_MEANING':
            // Merge words arrays - Word Meaning uses languageWord as unique identifier
            if (existing.words && content.words) {
              content.words.forEach(word => {
                if (!existing.words.find(w => w.languageWord === word.languageWord)) {
                  existing.words.push(word);
                }
              });
            }
            break;

          case 'DUAL_MEANING':
            // Merge words arrays - Dual Meaning uses languageWord as unique identifier
            if (existing.words && content.words) {
              content.words.forEach(word => {
                if (!existing.words.find(w => w.languageWord === word.languageWord)) {
                  existing.words.push(word);
                }
              });
            }
            break;

          case 'WORD_PRACTICE':
            // Merge words arrays - Word Practice uses englishWord as unique identifier
            if (existing.words && content.words) {
              content.words.forEach(word => {
                if (!existing.words.find(w => w.englishWord === word.englishWord)) {
                  existing.words.push(word);
                }
              });
            }
            break;

          case 'WORD_SPLIT':
            // Merge words arrays - Word Split uses question as unique identifier
            if (existing.words && content.words) {
              content.words.forEach(word => {
                if (!existing.words.find(w => (w.question || w.combinedWord) === (word.question || word.combinedWord))) {
                  existing.words.push(word);
                }
              });
            }
            break;

          case 'QUESTION_ANSWERS':
          case 'LONG_QUESTION_ANSWERS':
            // Merge questions arrays
            if (existing.questions && content.questions) {
              content.questions.forEach(q => {
                if (!existing.questions.find(eq => eq.questionKannada === q.questionKannada)) {
                  existing.questions.push(q);
                }
              });
            }
            break;

          case 'MATCH':
          case 'OPPOSITES':
          case 'WHO_SAID_TO_WHOM':
            // Merge pairs arrays
            if (existing.pairs && content.pairs) {
              content.pairs.forEach(pair => {
                if (!existing.pairs.find(p => p.left === pair.left && p.right === pair.right)) {
                  existing.pairs.push(pair);
                }
              });
            }
            break;

          case 'MAKE_SENTENCES':
            // Merge sentences arrays
            if (existing.sentences && content.sentences) {
              content.sentences.forEach(sentence => {
                if (!existing.sentences.find(s => s.englishText === sentence.englishText)) {
                  existing.sentences.push(sentence);
                }
              });
            }

            // Merge activity-level keywords
            if (existing.keywords && content.keywords) {
              content.keywords.forEach(keyword => {
                if (!existing.keywords.find(k => k.languageText === keyword.languageText && k.englishText === keyword.englishText)) {
                  existing.keywords.push(keyword);
                }
              });
            } else if (!existing.keywords && content.keywords) {
              existing.keywords = [...content.keywords];
            }
            break;

          case 'FILL_IN_BLANK_MCQ':
            // Merge questions arrays (MCQ format)
            if (existing.questions && content.questions) {
              content.questions.forEach(q => {
                if (!existing.questions.find(eq => eq.question === q.question)) {
                  existing.questions.push(q);
                }
              });
            }
            break;

          case 'NUMBERS':
          case 'GENDERS':
          case 'CHANGE_GENDERS':
          case 'SINGULAR_PLURAL':
          case 'TENSES':
            // Merge items arrays (generic structure)
            if (existing.items && content.items) {
              content.items.forEach(item => {
                // Use a generic comparison - check if item with same text exists
                const itemText = item.text || item.word || item.number || item.question;
                if (!existing.items.find(i => {
                  const existingText = i.text || i.word || i.number || i.question;
                  return existingText === itemText;
                })) {
                  existing.items.push(item);
                }
              });
            } else if (existing.words && content.words) {
              // Fallback to words array if items doesn't exist
              content.words.forEach(word => {
                if (!existing.words.find(w => w.word === word.word)) {
                  existing.words.push(word);
                }
              });
            } else if (existing.pairs && content.pairs) {
              // Fallback to pairs array
              content.pairs.forEach(pair => {
                if (!existing.pairs.find(p => p.left === pair.left && p.right === pair.right)) {
                  existing.pairs.push(pair);
                }
              });
            }
            break;

          case 'CREATIVE_WRITING':
            // Merge prompts arrays
            if (existing.prompts && content.prompts) {
              content.prompts.forEach(prompt => {
                if (!existing.prompts.find(p => p.title === prompt.title)) {
                  existing.prompts.push(prompt);
                }
              });
            }
            break;

          default:
            // For any unknown types, merge arrays generically
            Object.keys(content).forEach(key => {
              if (Array.isArray(existing[key]) && Array.isArray(content[key])) {
                // Generic array merge - avoid duplicates by stringifying
                content[key].forEach(item => {
                  const itemStr = JSON.stringify(item);
                  if (!existing[key].find(existingItem => JSON.stringify(existingItem) === itemStr)) {
                    existing[key].push(item);
                  }
                });
              }
            });
            break;
        }
      }
    });

    return practiceMap;
  };

  const convertToConfigJSON = (selectedChapters) => {
    const chapters = [];
    
    Object.entries(selectedChapters)
      .filter(([_, data]) => data.selected)
      .forEach(([chapterId]) => {
        chapters.push({ chapterId });
      });
    
    return {
      chapterConfig: JSON.stringify({ chapters })
    };
  };

  // Filter activity content based on selected items
  const filterActivityContent = (content, activityType, selectedItems) => {
    // If no specific items selected, return full content
    if (selectedItems.length === 0) return content;
    
    const filtered = { ...content };
    
    switch (activityType) {
      case 'WORD_MEANING':
      case 'WORD_PRACTICE':
      case 'WORD_SPLIT':
        if (content.words && Array.isArray(content.words)) {
          filtered.words = content.words.filter((_, index) => 
            selectedItems.includes(`word-${index}`)
          );
        }
        break;
      
      case 'QUESTION_ANSWERS':
      case 'LONG_QUESTION_ANSWERS':
        if (content.questions && Array.isArray(content.questions)) {
          filtered.questions = content.questions.filter((_, index) => 
            selectedItems.includes(`question-${index}`)
          );
        }
        break;
      
      case 'MATCH':
        if (content.pairs && Array.isArray(content.pairs)) {
          filtered.pairs = content.pairs.filter((_, index) => 
            selectedItems.includes(`pair-${index}`)
          );
        }
        break;
      
      case 'OPPOSITES':
        if (content.words && Array.isArray(content.words)) {
          filtered.words = content.words.filter((_, index) => 
            selectedItems.includes(`opposite-${index}`)
          );
        }
        break;
      
      case 'WHO_SAID_TO_WHOM':
        if (content.dialogues && Array.isArray(content.dialogues)) {
          filtered.dialogues = content.dialogues.filter((_, index) => 
            selectedItems.includes(`dialogue-${index}`)
          );
        }
        break;
      
      default:
        // For other activity types, return full content
        return content;
    }
    
    return filtered;
  };

  // Parse JSON config to selectedChapters format
  const parseConfigJSON = (configJSON) => {
    try {
      const parsed = JSON.parse(configJSON);
      const selectedChapters = {};
      
      if (parsed.chapters) {
        parsed.chapters.forEach(ch => {
          selectedChapters[ch.chapterId] = {
            selected: true,
            activities: {}
          };
          if (ch.activities) {
            ch.activities.forEach(act => {
              // Handle both old format (string) and new format (object with items)
              if (typeof act === 'string') {
                selectedChapters[ch.chapterId].activities[act] = { selected: true, items: {} };
              } else {
                selectedChapters[ch.chapterId].activities[act.activityId] = {
                  selected: true,
                  items: {}
                };
                if (act.items) {
                  act.items.forEach(itemId => {
                    selectedChapters[ch.chapterId].activities[act.activityId].items[itemId] = true;
                  });
                }
              }
            });
          }
        });
      }
      
      return selectedChapters;
    } catch (err) {
      console.error('Failed to parse config JSON:', err);
      return {};
    }
  };

  const handleCreateConfig = async () => {
    if (!user?.email) {
      setError('User authentication information is missing. Please try again.');
      return;
    }

    try {
      setCreating(true);
      setError(null);

      const userResult = await getUserByEmail({ email: user.email });
      
      if (userResult.errors && userResult.errors.length > 0) {
        throw new Error(userResult.errors[0].message || 'Failed to fetch user information');
      }

      if (!userResult.data?.users?.length) {
        throw new Error('User not found in the system. Please ensure your account is properly set up.');
      }

      const internalUserId = userResult.data.users[0].id;

      if (!selectedStudent?.gradeLevel) {
        throw new Error('Student grade level is missing. Cannot create configuration.');
      }

      const selectedChaptersCount = Object.values(newConfig.selectedChapters).filter(c => c.selected).length;
      if (selectedChaptersCount === 0) {
        throw new Error('Please select at least one chapter.');
      }

      if (!newConfig.configName?.trim()) {
        throw new Error('Please provide a name for this configuration.');
      }

      const { chapterConfig } = convertToConfigJSON(newConfig.selectedChapters);
      
      const configData = {
        grade: selectedStudent.gradeLevel,
        configName: newConfig.configName.trim(),
        chapterConfig: chapterConfig
      };

      let result;
      
      const effectivelySchoolLevel = selectedStudent.school?.id && (isGlobalAdmin || selectedStudent.isSchoolAdmin) && isSchoolLevelConfig;
      
      if (effectivelySchoolLevel) {
        result = await createSchoolChapterConfig({
          ...configData,
          schoolId: selectedStudent.school.id
        });
      } else {
        result = await createUserChapterConfig({
          ...configData,
          userId: internalUserId
        });
      }

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message || 'Failed to create configuration');
      }

      if (result.data) {
        
        // Extract the new config ID (Data Connect returns the key/id)
        const newId = result.data.chapterConfig_insert?.id || result.data.chapterConfig_insert;
        
        if (newId) {
          // Construct the new config object manually to update UI immediately
          const newConfigObject = {
            id: typeof newId === 'object' ? newId.id : newId,
            grade: selectedStudent.gradeLevel,
            configName: configData.configName,
            chapterConfig: configData.chapterConfig,
            isDefault: false,
            // Add user/school info based on what was used for creation
            ...(effectivelySchoolLevel && selectedStudent.school ? { 
              school: { id: selectedStudent.school.id, name: selectedStudent.school.name } 
            } : { 
              user: { id: internalUserId } 
            })
          };
          
          // Update local state immediately for instant feedback
          setConfigs(prev => [...prev, newConfigObject]);
        }

        // Reset form and close it
        setNewConfig({ configName: '', selectedChapters: {} });
        setShowCreateForm(false);
        setError(null);
        
        alert('Configuration created successfully!');

        // Trigger a background refresh to ensure consistency with server
        // Using a longer delay (3s) to allow for indexing and merging instead of replacing
        setTimeout(async () => {
          const params = {
            userId: internalUserId,
            grade: selectedStudent.gradeLevel,
            ...(selectedStudent.school?.id && { schoolId: selectedStudent.school.id })
          };
          try {
            const refreshResult = await getChapterConfigsBySchoolAndUser(params);
            if (refreshResult.data) {
              const serverConfigs = [
                ...(refreshResult.data.schoolConfigs || []),
                ...(refreshResult.data.userConfigs || [])
              ];
              
              setConfigs(prev => {
                // Merge server data with local state to ensure no "disappearing" configs
                const merged = [...serverConfigs];
                prev.forEach(localConfig => {
                  if (!merged.find(m => m.id === localConfig.id)) {
                    merged.push(localConfig);
                  }
                });
                return merged;
              });
            }
          } catch (refreshErr) {
            console.warn('Background refresh failed:', refreshErr);
          }
        }, 3000);
      } else {
        throw new Error('Failed to create configuration: No response data received.');
      }
    } catch (err) {
      console.error('Failed to create config:', err);
      setError(err.message || 'Failed to create configuration. Please try again.');
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setCreating(false);
    }
  };

  const handleConfigSelect = (config) => {
    // Store selected config in localStorage or pass to parent
    localStorage.setItem('selectedChapterConfig', JSON.stringify(config));
    // Navigate back to home
    if (onBack) {
      onBack();
    }
  };

  const handleDeleteConfig = async (id) => {
    if (!window.confirm('Are you sure you want to delete this configuration? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(id);
      setError(null);

      // Get the internal User UUID
      if (!user?.email) {
        setError('User authentication information is missing.');
        return;
      }

      const userResult = await getUserByEmail({ email: user.email });
      if (!userResult.data?.users?.length) {
        setError('User not found in the system.');
        return;
      }

      const internalUserId = userResult.data.users[0].id;

      // Find the config in the already loaded configs to verify ownership
      const configToDelete = configs.find(config => config.id === id);
      if (!configToDelete) {
        setError('Configuration not found.');
        return;
      }

      // Verify ownership using the already loaded config data
      // Allow deletion if:
      // 1. It's a personal config and user owns it
      // 2. It's a school config and user is an admin/school admin
      const isOwner = configToDelete.user?.id === internalUserId;
      const isAuthorizedAdmin = configToDelete.school && (isGlobalAdmin || selectedStudent.isSchoolAdmin);
      
      if (!isOwner && !isAuthorizedAdmin) {
        setError('You can only delete your own configurations or school configurations if you are an admin.');
        return;
      }
      // Proceed with deletion
      const deleteResult = await deleteUserChapterConfig({ 
        id: id
      });

      if (deleteResult.errors) console.error('Delete result errors:', deleteResult.errors);

      // Check if deletion was successful
      // Firebase Data Connect delete operations return the deleted object key on success
      if (deleteResult.data?.chapterConfig_delete && !deleteResult.errors) {
        
        // If the deleted config was the currently selected one, clear the selection
        if (selectedStudent.selectedConfigId === id) {
          try {
            const updateResult = await updateStudentSyllabus({
              id: selectedStudent.id,
              selectedConfigId: null
            });
          } catch (updateErr) {
            console.warn('Failed to unselect deleted config for student, but config was deleted:', updateErr);
          }
        }
        
        // Remove the deleted config from the local state
        setConfigs(prevConfigs => {
          const filtered = prevConfigs.filter(config => config.id !== id);
          return filtered;
        });
        setError(null);
        alert('Configuration deleted successfully.');
      } else if (deleteResult.data && !deleteResult.data.chapterConfig_delete && !deleteResult.errors) {
        console.warn('Delete mutation ran but no record was deleted. ID might be invalid:', id);
        throw new Error('Delete operation failed: Configuration not found. It might have already been deleted.');
      } else {
        console.error('Delete failed or returned errors:', deleteResult);
        throw new Error(deleteResult.errors?.[0]?.message || 'Delete operation failed. Please check your permissions.');
      }
    } catch (err) {
      console.error('Failed to delete config:', err);
      setError('Failed to delete configuration. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  const handleEditConfig = (config) => {
    setEditing(config.id);
    setEditConfig({
      configName: config.configName || '',
      selectedChapters: parseConfigJSON(config.chapterConfig)
    });
  };

  const handleSaveEdit = async () => {
    try {
      setError(null);

      if (!editConfig.configName?.trim()) {
        throw new Error('Please provide a name for this configuration.');
      }

      const { chapterConfig } = convertToConfigJSON(editConfig.selectedChapters);
      
      const updateResult = await updateChapterConfig({
        id: editing,
        configName: editConfig.configName.trim(),
        chapterConfig: chapterConfig
      });

      if (updateResult.data?.chapterConfig_update && !updateResult.errors) {
        setConfigs(prevConfigs => 
          prevConfigs.map(config => 
            config.id === editing 
              ? { ...config, configName: editConfig.configName.trim(), chapterConfig: chapterConfig }
              : config
          )
        );
        
        setEditing(null);
        setEditConfig({ configName: '', selectedChapters: {} });
      } else {
        throw new Error('Update operation failed');
      }
    } catch (err) {
      console.error('Failed to update config:', err);
      setError('Failed to update configuration. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditing(null);
    setEditConfig({ configName: '', selectedChapters: {} });
  };

  const handleSelectConfig = async (configId) => {
    if (!selectedStudent?.id) {
      setError('Student information is missing.');
      return;
    }

    try {
      setSelecting(configId);
      setError(null);

      const result = await updateStudentSyllabus({
        id: selectedStudent.id,
        selectedConfigId: configId
      });

      if (result.data?.student_update && !result.errors) {
        // Successfully updated - navigate back to home
        onBack();
      } else {
        throw new Error('Failed to update syllabus selection');
      }
    } catch (err) {
      console.error('Failed to select configuration:', err);
      setError('Failed to select configuration. Please try again.');
    } finally {
      setSelecting(null);
    }
  };

  const handleSetDefault = async (configId) => {
    const config = configs.find(c => c.id === configId);
    if (!config?.school?.id) {
      setError('Only school-level configs can be set as default.');
      return;
    }

    const configDisplayName = config.configName || (config.school ? `${config.school.name} Syllabus` : 'Personal Syllabus');
    if (!window.confirm(`Set "${configDisplayName}" as the default configuration for Grade ${config.grade} at ${config.school.name}?`)) {
      return;
    }

    try {
      setSelecting(configId);
      setError(null);

      const result = await setDefaultConfig({
        configId: configId,
        schoolId: config.school.id,
        grade: config.grade
      });

      if (result.data && !result.errors) {
        // Refresh configs to show updated default status
        if (!user?.email) {
          setError('User authentication information is missing.');
          return;
        }

        const userResult = await getUserByEmail({ email: user.email });
        if (!userResult.data?.users?.length) {
          setError('User not found in the system.');
          return;
        }

        const internalUserId = userResult.data.users[0].id;
        const params = {
          userId: internalUserId,
          grade: selectedStudent.gradeLevel,
          ...(selectedStudent.school?.id && { schoolId: selectedStudent.school.id })
        };
        const refreshResult = await getChapterConfigsBySchoolAndUser(params);
        if (refreshResult.data) {
          const allConfigs = [
            ...(refreshResult.data.schoolConfigs || []),
            ...(refreshResult.data.userConfigs || [])
          ];
          setConfigs(allConfigs);
        }
      } else {
        throw new Error('Failed to set default configuration');
      }
    } catch (err) {
      console.error('Failed to set default:', err);
      setError('Failed to set default configuration. Please try again.');
    } finally {
      setSelecting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1b2a] to-[#1b263b] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-cyan-400 text-lg">Loading chapter configurations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1b2a] to-[#1b263b] relative overflow-hidden">
      {/* Grid Pattern Overlay */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `
            linear-gradient(rgba(6, 182, 212, 0.2) 1px, transparent 1px),
            linear-gradient(90deg, rgba(6, 182, 212, 0.2) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}
      />

      {/* Main Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Chapter Configurations</h1>
          <p className="text-cyan-400 text-lg">Select or create a syllabus configuration for Grade {selectedStudent.gradeLevel}</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6">
            <p className="text-red-300">{error}</p>
          </div>
        )}

        {/* Create New Config Button */}
        <div className="text-center mb-8">
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white font-bold py-3 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg shadow-green-400/40 border-2 border-green-300"
          >
            <div className="flex items-center space-x-3">
              <span className="text-xl">➕</span>
              <span>Create New Configuration</span>
            </div>
          </button>
        </div>

        {/* Create Config Form */}
        {showCreateForm && (
          <div className="bg-[#0d1b2a]/80 backdrop-blur-sm border border-cyan-400/30 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">Create New Configuration</h3>
              {selectedStudent.school?.id && (user?.email?.includes('admin') || selectedStudent.isSchoolAdmin) && isSchoolLevelConfig && (
                <span className="text-xs bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-3 py-1 rounded-full">
                  School-Level Config
                </span>
              )}
              {selectedStudent.school?.id && (user?.email?.includes('admin') || selectedStudent.isSchoolAdmin) && !isSchoolLevelConfig && (
                <span className="text-xs bg-gradient-to-r from-purple-500 to-pink-600 text-white px-3 py-1 rounded-full">
                  Personal Config
                </span>
              )}
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-cyan-400 text-sm font-medium mb-2">
                  Configuration Name
                </label>
                <input
                  type="text"
                  value={newConfig.configName}
                  onChange={(e) => setNewConfig(prev => ({ ...prev, configName: e.target.value }))}
                  placeholder="e.g., Mid-Term Syllabus, Revision Config"
                  className="w-full bg-[#1b263b]/50 border border-cyan-400/30 rounded-lg py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent placeholder-gray-500"
                />
              </div>

              {/* School Level Config Checkbox - Only show for admins and school admins */}
              {selectedStudent.school?.id && (user?.email?.includes('admin') || selectedStudent.isSchoolAdmin) && (
                <div className="bg-[#1b263b]/30 border border-cyan-400/20 rounded-lg p-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isSchoolLevelConfig}
                      onChange={(e) => setIsSchoolLevelConfig(e.target.checked)}
                      className="w-5 h-5 text-cyan-500 bg-[#0d1b2a] border-cyan-400/50 rounded focus:ring-2 focus:ring-cyan-400 focus:ring-offset-0 cursor-pointer"
                    />
                    <div className="ml-3">
                      <span className="text-white font-medium">School Level Configuration</span>
                      <p className="text-gray-400 text-xs mt-1">
                        {isSchoolLevelConfig 
                          ? 'This config will be available to all students in the school' 
                          : 'This config will be personal and only visible to you'}
                      </p>
                    </div>
                  </label>
                </div>
              )}

              <div>
                <label className="block text-cyan-400 text-sm font-medium mb-2">
                  Select Chapters
                </label>
                
                {/* Chapter Type Tabs */}
                <div className="flex space-x-2 mb-4 border-b border-cyan-400/30">
                  {['LESSON', 'POEM', 'CREATIVE_WRITING', 'LETTER', 'NUMBERS', 'GRAMMAR'].map((type) => (
                    <button
                      key={type}
                      onClick={() => setActiveTab(type)}
                      className={`px-4 py-2 font-medium transition-all duration-200 ${
                        activeTab === type
                          ? 'text-cyan-400 border-b-2 border-cyan-400'
                          : 'text-gray-400 hover:text-cyan-300'
                      }`}
                    >
                      {type === 'CREATIVE_WRITING' ? 'Creative Writing' : type.charAt(0) + type.slice(1).toLowerCase() + 's'}
                    </button>
                  ))}
                </div>

                <div className="max-h-96 overflow-y-auto bg-[#1b263b]/50 border border-cyan-400/30 rounded-lg p-4">
                  {allChapters.filter(ch => ch.type === activeTab).length === 0 ? (
                    <p className="text-gray-400 text-sm">No {activeTab.toLowerCase()}s available for grade {selectedStudent.gradeLevel}</p>
                  ) : (
                    allChapters.filter(ch => ch.type === activeTab).map((chapter) => (
                      <div key={chapter.id} className="mb-3">
                        {/* Chapter Checkbox */}
                        <div className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            id={`create-chapter-${chapter.id}`}
                            checked={newConfig.selectedChapters[chapter.id]?.selected || false}
                            onChange={() => toggleChapter(chapter.id, true)}
                            className="mt-1 w-4 h-4 text-cyan-500 bg-gray-700 border-gray-600 rounded focus:ring-cyan-500"
                          />
                          <div className="flex-1">
                            <label htmlFor={`create-chapter-${chapter.id}`} className="text-white font-medium cursor-pointer">
                              {chapter.title}
                            </label>
                            <p className="text-gray-400 text-xs">{chapter.subject} • {chapter.type}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="flex space-x-4 mt-6">
              <button
                onClick={handleCreateConfig}
                disabled={creating}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? 'Creating...' : 'Create Configuration'}
              </button>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setNewConfig({ selectedChapters: {} });
                }}
                className="bg-gray-600 hover:bg-gray-500 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Edit Config Form */}
        {editing && (
          <div className="bg-[#0d1b2a]/80 backdrop-blur-sm border border-blue-400/30 rounded-xl p-6 mb-8">
            <h3 className="text-xl font-semibold text-white mb-4">Edit Configuration</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-blue-400 text-sm font-medium mb-2">
                  Configuration Name
                </label>
                <input
                  type="text"
                  value={editConfig.configName}
                  onChange={(e) => setEditConfig(prev => ({ ...prev, configName: e.target.value }))}
                  placeholder="e.g., Mid-Term Syllabus, Revision Config"
                  className="w-full bg-[#1b263b]/50 border border-blue-400/30 rounded-lg py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent placeholder-gray-500"
                />
              </div>

              <div>
                <label className="block text-blue-400 text-sm font-medium mb-2">
                  Select Chapters
                </label>
                
                {/* Chapter Type Tabs */}
                <div className="flex space-x-2 mb-4 border-b border-blue-400/30">
                  {['LESSON', 'POEM', 'CREATIVE_WRITING', 'LETTER', 'NUMBERS', 'GRAMMAR'].map((type) => (
                    <button
                      key={type}
                      onClick={() => setActiveTab(type)}
                      className={`px-4 py-2 font-medium transition-all duration-200 ${
                        activeTab === type
                          ? 'text-blue-400 border-b-2 border-blue-400'
                          : 'text-gray-400 hover:text-blue-300'
                      }`}
                    >
                      {type === 'CREATIVE_WRITING' ? 'Creative Writing' : type.charAt(0) + type.slice(1).toLowerCase() + 's'}
                    </button>
                  ))}
                </div>

                <div className="max-h-96 overflow-y-auto bg-[#1b263b]/50 border border-blue-400/30 rounded-lg p-4">
                  {allChapters.filter(ch => ch.type === activeTab).length === 0 ? (
                    <p className="text-gray-400 text-sm">No {activeTab.toLowerCase()}s available for grade {selectedStudent.gradeLevel}</p>
                  ) : (
                    allChapters.filter(ch => ch.type === activeTab).map((chapter) => (
                      <div key={chapter.id} className="mb-3">
                        {/* Chapter Checkbox */}
                        <div className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            id={`edit-chapter-${chapter.id}`}
                            checked={editConfig.selectedChapters[chapter.id]?.selected || false}
                            onChange={() => toggleChapter(chapter.id, false)}
                            className="mt-1 w-4 h-4 text-blue-500 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <label htmlFor={`edit-chapter-${chapter.id}`} className="text-white font-medium cursor-pointer">
                              {chapter.title}
                            </label>
                            <p className="text-gray-400 text-xs">{chapter.subject} • {chapter.type}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="flex space-x-4 mt-6">
              <button
                onClick={handleSaveEdit}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-400 hover:to-indigo-500 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300"
              >
                Save Changes
              </button>
              <button
                onClick={handleCancelEdit}
                className="bg-gray-600 hover:bg-gray-500 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Configs List */}
        {configs.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg mb-4">No configurations found for your grade.</div>
            <p className="text-gray-500">Create a new configuration to get started.</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {configs.map((config) => {
              const isSelected = selectedStudent.selectedConfigId === config.id;
              return (
              <div
                key={config.id}
                className={`backdrop-blur-sm rounded-xl p-6 transition-all duration-300 group relative ${
                  isSelected
                    ? 'bg-green-900/30 border-2 border-green-400 shadow-lg shadow-green-400/30'
                    : 'bg-[#0d1b2a]/50 border border-cyan-400/30 hover:bg-[#0d1b2a]/70 hover:border-cyan-400/50'
                }`}
              >
              {/* Action Buttons */}
              {/* Show edit/delete for personal configs OR school configs if user is admin/school admin */}
              {(() => {
                const isPersonalConfig = !config.school;
                const isAuthorizedForSchoolConfig = config.school && (isGlobalAdmin || selectedStudent.isSchoolAdmin);
                
                if (isPersonalConfig || isAuthorizedForSchoolConfig) {
                  return (
                    <div className="absolute top-4 right-4 flex space-x-2">
                      {/* Edit Button */}
                      <button
                        onClick={() => handleEditConfig(config)}
                        disabled={editing === config.id}
                        className="bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-lg p-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Edit Configuration"
                      >
                        <span className="text-blue-300 text-sm">
                          ✏️
                        </span>
                      </button>
                      {/* Delete Button - For personal configs OR school configs if authorized */}
                      <button
                        onClick={() => handleDeleteConfig(config.id)}
                        disabled={deleting === config.id}
                        className="bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg p-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete Configuration"
                      >
                        <span className="text-red-300 text-sm">
                          {deleting === config.id ? '⏳' : '🗑️'}
                        </span>
                      </button>
                    </div>
                  );
                }
                return null;
              })()}

                {/* Configuration Info */}
                <div className="mb-4">
                  <div className="flex items-center justify-between pr-20">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                        <span className="text-xl">📚</span>
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-lg">
                          {config.configName || (config.school ? `${config.school.name} Syllabus` : 'My Personal Syllabus')}
                        </h3>
                        <p className="text-cyan-400 text-sm">Grade {config.grade}</p>
                      </div>
                    </div>
                    {config.isDefault && (
                      <span className="bg-yellow-500/20 text-yellow-400 text-xs font-bold px-2 py-1 rounded border border-yellow-500/30">
                        DEFAULT
                      </span>
                    )}
                  </div>
                </div>

                {/* Config Details */}
                <div className="space-y-2 mb-4">
                  {config.school && (
                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-gray-300">
                        <span className="text-cyan-400 mr-2">🏫</span>
                        <span>{config.school.name}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="space-y-2">
                  {/* Select Button */}
                  <button
                    onClick={() => handleSelectConfig(config.id)}
                    disabled={selecting === config.id || selectedStudent.selectedConfigId === config.id}
                    className={`w-full font-semibold py-3 px-4 rounded-lg transition-all duration-300 transform shadow-lg ${
                      selectedStudent.selectedConfigId === config.id
                        ? 'bg-green-600 text-white cursor-default'
                        : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white group-hover:scale-105 shadow-cyan-500/25 disabled:opacity-50 disabled:cursor-not-allowed'
                    }`}
                  >
                    {selecting === config.id ? 'Selecting...' : selectedStudent.selectedConfigId === config.id ? '✓ Selected' : 'Select Configuration'}
                  </button>
                  
                  {/* Set as Default Button - Only for school configs and if user is admin/school admin */}
                  {config.school && (user?.email?.includes('admin') || selectedStudent.isSchoolAdmin) && !config.isDefault && (
                    <button
                      onClick={() => handleSetDefault(config.id)}
                      disabled={selecting === config.id}
                      className="w-full font-medium py-2 px-4 rounded-lg transition-all duration-300 bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ⭐ Set as School Default
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* Back Button */}
        <div className="text-center mt-8">
          <button
            onClick={onBack}
            className="bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            ← Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChapterConfigPage;
